import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import apiService from '../services/api';
import { CONTENT_VISIBILITY_STORAGE_KEY, normalizeContentVisibilityPreference, preferenceToApiView, } from '../utils/contentVisibility';
const DEFAULT_CONTENT_VISIBILITY_CONTEXT = {
    preference: 'accepted',
    effectiveView: 'wiki',
    saving: false,
    setPreference: async () => undefined,
};
const ContentVisibilityContext = createContext(DEFAULT_CONTENT_VISIBILITY_CONTEXT);
function readGuestPreference() {
    try {
        return normalizeContentVisibilityPreference(localStorage.getItem(CONTENT_VISIBILITY_STORAGE_KEY));
    }
    catch {
        return 'accepted';
    }
}
export const ContentVisibilityProvider = ({ children }) => {
    const { user, updateUser } = useAuth();
    const [preference, setPreferenceState] = useState(readGuestPreference);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        const next = user
            ? normalizeContentVisibilityPreference(user.preferences?.contentVisibility)
            : readGuestPreference();
        setPreferenceState(next);
    }, [user]);
    const setPreference = useCallback(async (next) => {
        const normalized = normalizeContentVisibilityPreference(next);
        const previous = preference;
        setPreferenceState(normalized);
        try {
            localStorage.setItem(CONTENT_VISIBILITY_STORAGE_KEY, normalized);
        }
        catch {
            // A blocked browser store must not make the control unusable for this session.
        }
        if (!user)
            return;
        setSaving(true);
        updateUser({ preferences: { ...user.preferences, contentVisibility: normalized } });
        try {
            await apiService.updateCurrentMemberPreferences({ contentVisibility: normalized });
        }
        catch (error) {
            setPreferenceState(previous);
            try {
                localStorage.setItem(CONTENT_VISIBILITY_STORAGE_KEY, previous);
            }
            catch {
                // Keep the in-memory rollback even when browser storage is unavailable.
            }
            updateUser({ preferences: { ...user.preferences, contentVisibility: previous } });
            throw error;
        }
        finally {
            setSaving(false);
        }
    }, [preference, updateUser, user]);
    const value = useMemo(() => ({
        preference,
        effectiveView: preferenceToApiView(preference),
        saving,
        setPreference,
    }), [preference, saving, setPreference]);
    return React.createElement(ContentVisibilityContext.Provider, { value: value }, children);
};
export const ContentVisibilityScope = ({ view, children }) => {
    const parent = useContentVisibility();
    const value = useMemo(() => ({ ...parent, effectiveView: view }), [parent, view]);
    return React.createElement(ContentVisibilityContext.Provider, { value: value }, children);
};
export function useContentVisibility() {
    return useContext(ContentVisibilityContext);
}
